import { useEffect, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { Icon } from './Icon';

// Copying to the clipboard: a Copy button, a value on one line with one, and copyable code.

/**
 * Copies text to the clipboard. Uses the Clipboard API where the page may, and a hidden text area with the
 * older copy command elsewhere (plain http on a LAN address, older browsers). Resolves false when both fail.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission refused or the document lost focus: try the fallback below.
  }
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.setAttribute('aria-hidden', 'true');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '-9999px';
  area.style.opacity = '0';
  // Inside an open dialog the text area has to sit in the dialog, or the focus trap pulls focus back out.
  const host = document.getElementById('dlg') ?? document.body;
  host.appendChild(area);
  area.select();
  area.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  active?.focus({ preventScroll: true });
  return ok;
}

/** Copies and says so in a toast. */
export async function copyWithToast(text: string, what: string): Promise<boolean> {
  const ok = await copyText(text);
  if (ok) toast(`${what} copied`, { icon: 'copy' });
  else toast('Your browser blocked copying. Select the text and copy it with your keyboard.', { error: true });
  return ok;
}

export function CopyButton({
  text,
  what,
  label = 'Copy',
  className = 'btn btn-sm btn-tile',
  autoFocus,
}: {
  text: string;
  /** Names the copied thing in the toast and for screen readers: "Invite link". */
  what: string;
  label?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      className={className}
      aria-label={`Copy ${what.toLowerCase()}`}
      data-autofocus={autoFocus || undefined}
      onClick={async () => {
        if (await copyWithToast(text, what)) {
          setDone(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setDone(false), 2000);
        }
      }}
    >
      <Icon name={done ? 'check' : 'copy'} />
      {done ? 'Copied' : label}
    </button>
  );
}

/** A value on one line with a Copy button, such as an invite link. */
export function CopyField({ value, what, autoFocus }: { value: string; what: string; autoFocus?: boolean }) {
  return (
    <div className="copy-field">
      <span title={value}>{value}</span>
      <CopyButton text={value} what={what} autoFocus={autoFocus} />
    </div>
  );
}

/** Copyable JSON or text, shown as a scrollable block. */
export function CodeBlock({ code, what }: { code: string; what: string }) {
  return (
    <div className="code-block">
      <pre tabIndex={0} aria-label={what}>
        <code>{code}</code>
      </pre>
      <CopyButton text={code} what={what} />
    </div>
  );
}
